/**
 * The claim held, as a small card on the field (D354) — the owner's design.
 *
 * The design takes the node card out of the thread and floats a compact one over
 * the graph's lower left: what kind of claim it is, in its hue; its label and its
 * claim; where it came from and the card's own state words; and two presses —
 * Details, which opens the whole card at the head of the thread (`openDetails`,
 * then `pokeInspect`, the scrubber's own door), and Focus, the foot's own press. The ✕ puts the claim
 * down: a selection cleared, as Escape clears it.
 *
 * It READS and does not act, the card's half of D347: nothing here spends, so
 * there is no price to show (invariant 9) — a verb is the composer's, in the
 * mode under the claim's line. It moves no camera (invariant 11). Its words are
 * the card's: the kind from `kindLabelOf`, the state from `stateClauses` — the one
 * list the card, the strip and the path read — so the two cannot describe one
 * claim two ways.
 *
 * Desktop, with the thread open, while the claim's details are not already open
 * in the thread (`cardDetails.ts`). It floats over the field, so it is the glass
 * (the glass law); the columns beside it are solid (D353).
 */

import { useStore } from '../core/store'
import { claimRestatesLabel, kindChannelOf, kindLabelOf } from '../core/types'
import { refutedUnder } from '../core/taint'
import { secondLookChange } from '../core/statusWords'
import { evidenceStaleOf } from '../core/rebase'
import { stateClauses } from '../core/stateClauses'
import { glass } from './components/glass'
import { Icon } from './components/Icon'
import { openDetails, useDetailsFor } from './cardDetails'
import { focusCardTitle } from './cardFocus'

export function FieldCard() {
  const selectedId = useStore((s) => s.selectedId)
  const session = useStore((s) => s.session)
  const focusId = useStore((s) => s.focusId)
  const setFocus = useStore((s) => s.setFocus)
  const select = useStore((s) => s.select)
  const pokeInspect = useStore((s) => s.pokeInspect)
  const details = useDetailsFor()
  const node = selectedId ? (session.nodes[selectedId] ?? null) : null
  if (!node || details === node.id) return null

  const tint = `var(--sem-${kindChannelOf(node)})`
  const parent = node.parentId ? (session.nodes[node.parentId] ?? null) : null
  // Where it came from, as the design says it: the seed by that name, any other
  // parent by its label.
  const from = parent
    ? parent.parentId === null
      ? 'From the seed'
      : `From “${parent.label}”`
    : null
  const refuted = refutedUnder(session, node.id)
  const evidenceChange = secondLookChange(evidenceStaleOf(session, node.id)?.changes, refuted)
  const line = [from, ...stateClauses(node, { refuted, evidenceChange }).map((c) => c.word)]
    .filter(Boolean)
    .join(' · ')
  // The clauses are written to follow a word, as the strip prints them; a line
  // that opens on one opens with a capital, as the card's own status line does.
  const meta = line && line.charAt(0).toUpperCase() + line.slice(1)
  const focused = focusId === node.id

  return (
    <section
      className="fieldcard"
      aria-label={`The claim held: ${node.label}`}
      style={{ ...glass(), borderRadius: 12 }}
    >
      <div className="fieldcard-head">
        <span className="fieldcard-kind" style={{ color: tint }}>
          <i aria-hidden style={{ background: tint }} />
          {kindLabelOf(node)}
        </span>
        <button
          type="button"
          className="fieldcard-x"
          aria-label="Put the claim down"
          title="Put the claim down  Esc"
          onClick={() => select(null)}
        >
          <Icon name="close" size={13} />
        </button>
      </div>
      <div className="fieldcard-body">
        <b className="fieldcard-title">{node.label}</b>
        {!claimRestatesLabel(node) && <p className="fieldcard-claim">{node.claim}</p>}
        {meta && <p className="fieldcard-meta">{meta}</p>}
      </div>
      <div className="fieldcard-acts">
        <button
          type="button"
          className="fieldcard-btn"
          data-details=""
          title="Its whole card — sources, results, the rewrite and the readings — at the head of the thread"
          onClick={() => {
            // The state first, then the poke: a thread that the poke itself
            // switches to the claim mounts its surface after the bump it would
            // have read, and swallows it (D42's frame) — the state it reads on
            // mount does not. The poke brings the column and the scroll; the
            // title takes the focus this card's press goes with.
            openDetails(node.id)
            pokeInspect()
            focusCardTitle(node.id)
          }}
        >
          Details
        </button>
        <button
          type="button"
          className="fieldcard-btn"
          aria-pressed={focused}
          title="Dim everything outside this node’s lineage and children"
          onClick={() => setFocus(focused ? null : node.id)}
        >
          {focused ? 'Unfocus' : 'Focus'}
        </button>
      </div>
    </section>
  )
}
